// helix-exception: local tooltip portals to document.body; the Flowbite Tooltip Helix re-exports renders inline and is clipped by truncate (F-09)
import { useLayoutEffect, useRef, useState, type ElementType, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'

import { useIsTruncated } from '@/components/common/useIsTruncated'
import { useTooltipTrigger } from '@/components/common/useTooltipTrigger'

/** Gap (px) between the anchor edge and the bubble. */
const ANCHOR_OFFSET = 6
/** Minimum gap (px) kept between the bubble and the viewport edges. */
const VIEWPORT_GUTTER = 8

export type TooltipPlacement = 'top' | 'bottom'

interface TooltipBubbleProps {
  id: string
  /** Element the bubble is positioned against. */
  anchorRef: RefObject<HTMLElement | null>
  children: ReactNode
  placement?: TooltipPlacement
}

/**
 * The tooltip bubble itself, rendered in a portal on `document.body` with
 * viewport (`fixed`) coordinates.
 *
 * The portal is load-bearing, not decoration: every value we attach a tooltip to
 * is clipped by `truncate` (`overflow: hidden`), so a bubble rendered inside the
 * trigger would be clipped by the very thing it exists to work around. Same
 * reasoning as the source-mark tooltip in `ClinicalSummaryCard`.
 *
 * Positioning is two-pass — render invisibly, measure, then commit coordinates —
 * because the bubble's width depends on how its text wraps under `max-w`, and we
 * need that width to keep it inside the viewport. Both passes happen inside a
 * layout effect, so nothing unpositioned is ever painted.
 */
export function TooltipBubble({ id, anchorRef, children, placement = 'bottom' }: TooltipBubbleProps) {
  const bubbleRef = useRef<HTMLDivElement>(null)
  const [frame, setFrame] = useState<{ top: number; left: number } | null>(null)

  useLayoutEffect(() => {
    const place = () => {
      const anchor = anchorRef.current
      const bubble = bubbleRef.current
      if (!anchor || !bubble) return

      const a = anchor.getBoundingClientRect()
      const b = bubble.getBoundingClientRect()

      const top = placement === 'top' ? a.top - b.height - ANCHOR_OFFSET : a.bottom + ANCHOR_OFFSET

      // The bubble is centred on the anchor via `-translate-x-1/2`, so clamping
      // the centre point keeps both edges inside the gutters. `max-w` caps the
      // bubble below the viewport width, which guarantees `min <= max`; the
      // branch is a guard for a caller that overrides the width.
      const half = b.width / 2
      const min = VIEWPORT_GUTTER + half
      const max = window.innerWidth - VIEWPORT_GUTTER - half
      const centre = a.left + a.width / 2
      const left = max < min ? min : Math.min(Math.max(centre, min), max)

      setFrame((prev) => (prev && prev.top === top && prev.left === left ? prev : { top, left }))
    }

    place()
    window.addEventListener('resize', place)
    // Capture phase: the order header is `sticky`, so the anchor moves whenever
    // any ancestor scroll container scrolls, not just the window.
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
    // `children` is a dependency because the bubble's own measured box feeds the
    // result: `placement="top"` subtracts the bubble height, so content that
    // changes while the bubble stays open (CopyableValue swaps a two-line
    // "value + Click to copy" for a one-line "Copied") would otherwise keep
    // coordinates from the old box and detach from its anchor.
  }, [anchorRef, placement, children])

  return createPortal(
    <div
      ref={bubbleRef}
      id={id}
      role="tooltip"
      // Truncation is purely visual — the trigger's DOM text is always the full
      // value, so assistive tech already reads it from the trigger. Exposing the
      // bubble as well would announce the same string twice.
      aria-hidden
      style={{
        top: frame?.top ?? 0,
        left: frame?.left ?? 0,
        visibility: frame ? 'visible' : 'hidden',
      }}
      className="pointer-events-none fixed z-50 max-w-[min(20rem,calc(100vw-1rem))] -translate-x-1/2 break-words rounded-base bg-background-dark px-2 py-1 text-xs font-medium text-white shadow-lg dark:bg-gray-700"
    >
      {children}
    </div>,
    document.body
  )
}

interface TooltipProps {
  /** Bubble content. Nothing renders when this is empty. */
  content: ReactNode
  /** The trigger. Wrapped in an inline-flex span that owns the hover/focus handlers. */
  children: ReactNode
  /** Suppress the tooltip entirely (e.g. there is nothing extra to reveal). */
  disabled?: boolean
  placement?: TooltipPlacement
  /** Extra classes for the wrapper span. */
  className?: string
}

/**
 * Hover/focus tooltip around an arbitrary trigger. Use this when the trigger is
 * already an interactive element (button, link); for revealing text that is only
 * clipped at narrow widths prefer {@link TruncatedText}, which measures first.
 */
export function Tooltip({ content, children, disabled = false, placement = 'bottom', className }: TooltipProps) {
  const anchorRef = useRef<HTMLSpanElement>(null)
  const { open, tooltipId, triggerProps } = useTooltipTrigger({ disabled: disabled || !content })

  return (
    <span ref={anchorRef} className={`inline-flex min-w-0 max-w-full ${className ?? ''}`} {...triggerProps}>
      {children}
      {open ? (
        <TooltipBubble id={tooltipId} anchorRef={anchorRef} placement={placement}>
          {content}
        </TooltipBubble>
      ) : null}
    </span>
  )
}

/** Tags this component is intended to render. Keeps the polymorphic surface honest. */
export type TruncatedTextTag = 'span' | 'div' | 'p' | 'h1' | 'h2' | 'h3'

interface TruncatedTextProps {
  /** The text to render; also the tooltip content when it does not fit. */
  text: string
  /** Element to render. Defaults to `span`. */
  as?: TruncatedTextTag
  /** Extra classes. `block truncate` is always applied. */
  className?: string
  /**
   * Whether the clipped text becomes keyboard-focusable so keyboard users can
   * reveal it. Defaults to true, and only applies while the text is clipped — a
   * value that fits never becomes a tab stop. Disable where an ancestor is
   * already focusable and surfaces the full value itself.
   */
  focusable?: boolean
  placement?: TooltipPlacement
}

/**
 * Text that ellipsises when it does not fit and reveals the full value in a
 * tooltip on hover/focus — but *only* while it is actually clipped.
 *
 * This is the Epic-sidebar case: at full workspace width the patient name fits
 * and behaves like plain text (no tooltip, no tab stop); in the ~350px sidebar
 * the same name is ellipsised and becomes hoverable/focusable to reveal it.
 */
export function TruncatedText({
  text,
  as = 'span',
  className,
  focusable = true,
  placement = 'bottom',
}: TruncatedTextProps) {
  const ref = useRef<HTMLElement>(null)
  const isTruncated = useIsTruncated(ref)
  const { open, tooltipId, triggerProps } = useTooltipTrigger({ disabled: !isTruncated })
  const Component = as as ElementType

  return (
    <>
      <Component
        ref={ref}
        className={`block truncate ${className ?? ''}`}
        // Handlers and the tab stop only exist while the text is clipped, so a
        // fully visible value stays inert, non-focusable plain text.
        {...(isTruncated ? { ...triggerProps, tabIndex: focusable ? 0 : undefined } : {})}
      >
        {text}
      </Component>
      {open ? (
        <TooltipBubble id={tooltipId} anchorRef={ref} placement={placement}>
          {text}
        </TooltipBubble>
      ) : null}
    </>
  )
}
